import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type { ImageFormat, FitMode } from '@sanity/image-url/lib/types/types';
import { logger } from './logger';

// Sanity client configuration
export const client = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || 'o12a3h5d',
  dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
  useCdn: true, // Use CDN for faster reads (data is cached)
  apiVersion: '2024-01-01', // Use current date for API version
});

// Image URL builder
const builder = imageUrlBuilder(client);

/**
 * Generate optimized image URL from Sanity image reference
 * @param {Object} source - Sanity image object
 * @returns {Function} - Image URL builder with chainable methods
 */
export function urlFor(source) {
  return builder.image(source);
}

/**
 * Get image URL with default optimizations and hotspot support
 * @param {Object} source - Sanity image object (includes hotspot/crop data)
 * @param {Object} options - Optional size/format options
 * @returns {string} - Optimized image URL
 */
interface ImageOptions {
  width?: number;
  height?: number;
  format?: ImageFormat;
  quality?: number;
  fit?: FitMode;
}

export function getImageUrl(source: unknown, options: ImageOptions = {}): string {
  if (!source) return '';

  const { width = 800, height, format = 'webp', quality = 80, fit = 'max' } = options;

  try {
    let url = builder.image(source).format(format).quality(quality);

    // When using fit='crop', the builder automatically uses hotspot data
    if (fit) url = url.fit(fit);
    if (width) url = url.width(width);
    if (height) url = url.height(height);

    return url.url();
  } catch (err) {
    // An unresolvable image ref (e.g. a deleted asset still referenced in Sanity)
    // makes the URL builder throw. Returning '' isolates the failure to this one
    // image so a single bad reference can't break the whole artwork catalog,
    // since getImageUrl runs inside transformArtwork across every artwork.
    logger.warn('Failed to build image URL from Sanity source:', err);
    return '';
  }
}

/**
 * Derive a clean download filename from a Sanity image URL.
 * getImageUrl() returns full CDN URLs with a query string
 * (e.g. ".../abc-1200x800.png?w=1200&fmt=webp"), which are unusable as a
 * download filename or ZIP entry name: the path separators turn into nested
 * folders and the query string contains characters that are invalid in
 * filenames on Windows. This strips the query and path, leaving just the asset
 * filename, and falls back when no usable segment is present.
 * @param {string} url - Full image URL (or empty/undefined)
 * @param {string} fallback - Name to use when no filename can be extracted
 * @returns {string} - Clean filename safe for downloads/ZIP entries
 */
export function getImageFileName(url: string | null | undefined, fallback: string): string {
  if (!url) return fallback;
  const pathPart = url.split('?')[0];
  const segment = pathPart.substring(pathPart.lastIndexOf('/') + 1);
  return segment || fallback;
}

/**
 * Get CSS object-position value from Sanity image hotspot
 * Use this when displaying images with object-fit: cover to ensure
 * the hotspot (focal point) stays visible
 * @param {Object} source - Sanity image object with hotspot data
 * @returns {string} - CSS object-position value (e.g., "30% 20%")
 */
export function getImageHotspot(source) {
  if (!source?.hotspot) return 'center center';

  const { x, y } = source.hotspot;
  // Hotspot values are 0-1, convert to percentage
  return `${Math.round(x * 100)}% ${Math.round(y * 100)}%`;
}

// GROQ Queries
export const queries = {
  // Get all artworks (sorted by ID)
  allArtworks: `*[_type == "artwork"] | order(id asc) {
    id,
    name,
    location,
    artistCulture,
    dateRange,
    materials,
    unit,
    museum,
    displayedLocation,
    displayedCoordinates,
    originatedCoordinates,
    "images": images[] {
      ...,
      hotspot,
      crop,
      asset->
    },
    videos
  }`,

  // Get single artwork by ID
  artworkById: (id) => `*[_type == "artwork" && id == ${id}][0] {
    id,
    name,
    location,
    artistCulture,
    dateRange,
    materials,
    unit,
    museum,
    displayedLocation,
    displayedCoordinates,
    originatedCoordinates,
    "images": images[] {
      ...,
      hotspot,
      crop,
      asset->
    },
    videos
  }`,

  // Get all due dates with artwork references expanded
  allDueDates: `*[_type == "dueDate"] | order(dueDate asc) {
    _id,
    type,
    title,
    dueDate,
    notes,
    "artwork": artwork-> {
      id,
      name
    }
  }`,

  // Get due dates for a date range
  dueDatesInRange: (startDate, endDate) => `*[_type == "dueDate" && dueDate >= "${startDate}" && dueDate <= "${endDate}"] | order(dueDate asc) {
    _id,
    type,
    title,
    dueDate,
    notes,
    "artwork": artwork-> {
      id,
      name
    }
  }`,
};

/**
 * Transform Sanity artwork to match legacy JSON structure
 * This allows existing components to work without modification
 */
export function transformArtwork(sanityArtwork) {
  if (!sanityArtwork) return null;

  const { dateRange, displayedCoordinates, originatedCoordinates, images, videos, artistCulture, ...rest } = sanityArtwork;

  // Convert dateRange object to legacy string format
  let date = '';
  if (dateRange) {
    if (dateRange.startYear === dateRange.endYear) {
      date = String(dateRange.startYear);
    } else {
      date = `${dateRange.startYear}/${dateRange.endYear}`;
    }
  }

  // Convert images array to legacy format (array of URLs) for backward compatibility
  const image = images?.map((img) => getImageUrl(img, { width: 1200 })) || [];

  // Also keep raw image data with hotspot info for components that need it
  const imageData = images || [];

  // Convert videos array to legacy format. videoLink and transcript are
  // parallel arrays paired by index (VideoPlayer zips them via transcript[i]),
  // so both must be derived from the SAME filtered set of videos. Filtering
  // each independently let a video with a URL but no transcript shift every
  // later transcript onto the wrong video. Videos lacking a transcript get an
  // empty-transcript placeholder ("[]") instead of being dropped.
  const usableVideos = videos?.filter((v) => v.url) || [];
  const videoLink = usableVideos.map((v) => v.url);
  const transcript = usableVideos.map((v) =>
    v.transcript ? JSON.stringify(v.transcript) : "[]"
  );

  return {
    ...rest,
    artist_culture: artistCulture || 'None',
    date,
    displayedLatitude: displayedCoordinates?.latitude,
    displayedLongitude: displayedCoordinates?.longitude,
    originatedLatitude: originatedCoordinates?.latitude,
    originatedLongitude: originatedCoordinates?.longitude,
    image,
    imageData, // Raw Sanity image objects with hotspot/crop data
    videoLink: videoLink?.length ? videoLink : null,
    transcript: transcript?.length ? transcript : null,
  };
}

/**
 * Transform Sanity due dates to match legacy DueDates.json structure
 */
export function transformDueDates(sanityDueDates) {
  const assignments = [];
  const quizzes = [];

  if (!Array.isArray(sanityDueDates)) {
    return { assignments, quizzes };
  }

  sanityDueDates.forEach((item) => {
    // Convert YYYY-MM-DD to M-D format
    const dateParts = item.dueDate?.split('-');
    let formattedDate = '';
    if (dateParts && dateParts.length >= 3) {
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);
      if (!Number.isNaN(month) && !Number.isNaN(day)) {
        formattedDate = `${month}-${day}`;
      }
    }

    if (item.type === 'artwork' && item.artwork) {
      assignments.push({
        id: String(item.artwork.id),
        dueDate: formattedDate,
      });
    } else if (item.type === 'event') {
      assignments.push({
        id: item.title,
        dueDate: formattedDate,
      });
    } else if (item.type === 'quiz') {
      quizzes.push({
        title: item.title,
        dueDate: formattedDate,
      });
    }
  });

  return { assignments, quizzes };
}
